/**
 * Cat Card Component for Marketplace
 */

import { Link } from 'react-router-dom';
import { Heart, MapPin, Calendar, Info } from 'lucide-react';
import { Badge } from '../shared/UIComponents';
import { formatCurrency, getCatAge, parseEmsCode, cn } from '../../utils/helpers';

// Placeholder cat image
const PLACEHOLDER_CAT = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'%3E%3Crect fill='%23f3f4f6' width='400' height='300'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23d1d5db' font-family='system-ui' font-size='48'%3E🐱%3C/text%3E%3C/svg%3E";

export const CatCard = ({ 
  cat, 
  showPrice = true,
  showStatus = true,
  compact = false,
  className = ''
}) => {
  const { description: colorDescription } = parseEmsCode(cat.ems);
  const age = getCatAge(cat.dateOfBirth);
  const mainImage = cat.photos?.[0] || PLACEHOLDER_CAT;

  return (
    <Link 
      to={`/cat/${cat.id}`}
      className={cn(
        'group block bg-white rounded-xl overflow-hidden shadow-card transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1',
        className
      )}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
        <img
          src={mainImage}
          alt={cat.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={(e) => { e.target.src = PLACEHOLDER_CAT; }}
        />
        
        {/* Status badge */}
        {showStatus && (
          <div className="absolute top-3 left-3">
            <Badge status={cat.status} />
          </div>
        )}

        {/* Favorite button */}
        <button 
          onClick={(e) => { e.preventDefault(); }}
          className="absolute top-3 right-3 w-8 h-8 bg-white/80 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors"
        >
          <Heart className="w-4 h-4" />
        </button>

        {/* Price overlay */}
        {showPrice && cat.price && cat.status !== 'sold' && (
          <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-lg">
            <span className="font-bold text-feline-600">{formatCurrency(cat.price)}</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className={cn('p-4', compact && 'p-3')}>
        {/* Name and breed */}
        <div className="mb-2">
          <h3 className={cn(
            'font-display font-semibold text-gray-900 group-hover:text-feline-600 transition-colors',
            compact ? 'text-base' : 'text-lg'
          )}>
            {cat.name}
          </h3>
          <p className="text-sm text-gray-500">{cat.breed}</p>
        </div>

        {/* Details */}
        {!compact && (
          <div className="space-y-1.5 text-sm text-gray-600">
            {colorDescription && (
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-gradient-to-br from-feline-300 to-feline-500" />
                <span>{colorDescription}</span>
              </div>
            )}
            
            {age && (
              <div className="flex items-center gap-2">
                <Calendar className="w-3 h-3 text-gray-400" />
                <span>{age}</span>
              </div>
            )}

            {cat.sex && (
              <div className="flex items-center gap-2">
                <span className={cn(
                  'w-3 h-3 rounded-full',
                  cat.sex === 'male' ? 'bg-blue-400' : 'bg-pink-400'
                )} />
                <span className="capitalize">{cat.sex}</span>
              </div>
            )}
          </div>
        )}

        {/* Cattery info */}
        {cat.catteryName && (
          <div className={cn(
            'flex items-center gap-1.5 text-xs text-gray-400',
            compact ? 'mt-2' : 'mt-3 pt-3 border-t border-gray-100'
          )}>
            <MapPin className="w-3 h-3" />
            <span>{cat.catteryName}</span>
          </div>
        )}
      </div>
    </Link>
  );
};

// Horizontal cat card for lists
export const CatCardHorizontal = ({ cat, onEdit, onDelete, onStatusChange }) => {
  const { description: colorDescription } = parseEmsCode(cat.ems);
  const age = getCatAge(cat.dateOfBirth);
  const mainImage = cat.photos?.[0] || PLACEHOLDER_CAT;

  return (
    <div className="flex bg-white rounded-xl overflow-hidden shadow-card hover:shadow-card-hover transition-all">
      {/* Image */}
      <div className="w-32 h-32 flex-shrink-0 bg-gray-100">
        <img
          src={mainImage}
          alt={cat.name}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.src = PLACEHOLDER_CAT; }}
        />
      </div>

      {/* Content */}
      <div className="flex-1 p-4 flex flex-col justify-between">
        <div>
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">{cat.name}</h3>
              <p className="text-sm text-gray-500">{cat.breed} • {colorDescription}</p>
            </div>
            <Badge status={cat.status} />
          </div>
          
          <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
            {age && <span>{age}</span>}
            {cat.sex && <span className="capitalize">{cat.sex}</span>}
            {cat.microchip && <span>Microchip: {cat.microchip}</span>}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
          <div className="text-lg font-bold text-feline-600">
            {cat.price ? formatCurrency(cat.price) : 'Not listed'}
          </div>
          
          <div className="flex items-center gap-2">
            {onEdit && (
              <button
                onClick={() => onEdit(cat)}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-feline-600 hover:bg-feline-50 rounded-lg transition-colors"
              >
                Edit
              </button>
            )}
            {onStatusChange && (
              <select
                value={cat.status}
                onChange={(e) => onStatusChange(cat.id, e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-feline-300 focus:border-feline-500 outline-none"
              >
                <option value="not_for_sale">Not for Sale</option>
                <option value="available">Available</option>
                <option value="reserved">Reserved</option>
                <option value="sold">Sold</option>
              </select>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Cat grid component
export const CatGrid = ({ cats, loading, emptyMessage = 'No cats found' }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl overflow-hidden shadow-card animate-pulse">
            <div className="aspect-[4/3] bg-gray-200" />
            <div className="p-4 space-y-3">
              <div className="h-5 bg-gray-200 rounded w-3/4" />
              <div className="h-4 bg-gray-200 rounded w-1/2" />
              <div className="h-4 bg-gray-200 rounded w-2/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!cats || cats.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {cats.map((cat, index) => (
        <div 
          key={cat.id} 
          className="animate-slide-up"
          style={{ animationDelay: `${index * 0.05}s` }}
        >
          <CatCard cat={cat} />
        </div>
      ))}
    </div>
  );
};
